import { ElementType } from "react";
import {
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Text,
} from "@chakra-ui/react";
import { MdEmail } from "react-icons/md";
import { Lead, Organization } from "../utils/types";

interface EmailButtonProps {
  lead: Lead;
  organization: Organization;
  iconOnly?: boolean;
  size?: "sm" | "md" | "lg";
  text?: string;
}

interface EmailTemplate {
  id: number;
  title: string;
  subject: string;
  body: string;
}

// Example templates — you can expand or fetch from API
const templates = (orgName: string): EmailTemplate[] => [
  {
    id: 1,
    title: "Introduction / Follow-up",
    subject: `Follow-up from ${orgName}`,
    body: `Hi, this is the team at ${orgName}. We wanted to follow up regarding your interest and discuss next steps.`,
  },
  {
    id: 2,
    title: "Product Demo",
    subject: `Schedule a demo with ${orgName}`,
    body: `Hello, we at ${orgName} would love to show you our product in action. Are you available for a demo?`,
  },
  {
    id: 3,
    title: "Special Offer",
    subject: `Exclusive offer from ${orgName}`,
    body: `Hi, this is ${orgName}. We have a special offer for you and would love to share the details.`,
  },
  {
    id: 4,
    title: "Check-in",
    subject: `Checking in from ${orgName}`,
    body: `Hello, we at ${orgName} are checking in to see if you have any questions or need further info.`,
  },
  {
    id: 5,
    title: "Proposal / Quote",
    subject: `Proposal from ${orgName}`,
    body: `Hi, attached is the proposal from ${orgName}. We’d love your feedback and to discuss next steps.`,
  },
];

export default function EmailButton({
  lead,
  organization,
  iconOnly = false,
  size = "md",
  text = "Send Email",
  ...props
}: EmailButtonProps) {
  const handleTemplateClick = (template: EmailTemplate) => {
    // Prefill lead email, subject, body
    const email = encodeURIComponent(lead.email);
    const subject = encodeURIComponent(template.subject);
    const body = encodeURIComponent(template.body);

    // Open email client
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
  };

  return (
    <Menu>
      <MenuButton
        as={Button}
        leftIcon={!iconOnly ? <Icon as={MdEmail as ElementType} boxSize={5} /> : undefined}
        size={size}
        colorScheme="red"
        {...props}
      >
        {!iconOnly && text}
        {iconOnly && <Icon as={MdEmail as ElementType} />}
      </MenuButton>
      <MenuList>
        {templates(organization.name).map((t) => (
          <MenuItem key={t.id} onClick={() => handleTemplateClick(t)}>
            <Text fontWeight="semibold">{t.title}</Text>
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
